











































































































import { Component, Vue } from 'vue-property-decorator'
import { GamificationBadgeAPI, GamificationScoreAPI, GamificationSettingsAPI } from '@/api/backend-client'
import { User } from '@/api/model/user'
import { GamificationBadge } from '@/api/model/gamification-badge'
import { GamificationScore } from '@/api/model/gamification-score'
import { BadgeColor, BadgeColorCodes, BadgeType, BadgeTypes } from '@/api/types'

@Component
export default class GamificationProgress extends Vue {
    loading: boolean = false
    closed: boolean = false

    async mounted() {
        this.loading = true
        await this.reloadCompanySettings()
        await this.reloadCompanyScoresAndBadges()
        this.loading = false
    }

    async reloadCompanyScoresAndBadges(): Promise<void> {
        const [gamificationScores, gamificationBadges] = await Promise.all([
            this.loadGamificationScoresPerUser(),
            this.loadGamificationBadgesPerUser(),
        ])

        for (const user of this.$currentCompany.users) {
            if (!user || !user.id) {
                continue
            }

            if (user.resourceIdentifier in gamificationScores) {
                user.gamificationScores = gamificationScores[user.resourceIdentifier]
            }

            if (user.resourceIdentifier in gamificationBadges) {
                user.gamificationBadges = gamificationBadges[user.resourceIdentifier]
            }

            if (user.id === this.$currentUser.id) {
                this.$currentUser.gamificationScores = user.gamificationScores
                this.$currentUser.gamificationBadges = user.gamificationBadges
            }
        }
    }

    async loadGamificationScoresPerUser() {
        // Get gamification scores
        const gamificationScores = await GamificationScoreAPI.getAll({
            'user.company': this.$currentCompany.id,
        })

        // Get gamification scores indexed per user
        const gamificationScoresPerUser = {}

        gamificationScores.forEach((gamificationScore: GamificationScore) => {
            const user = gamificationScore.user instanceof User ? gamificationScore.user.id : gamificationScore.user as string
            if (!(user in gamificationScoresPerUser)) {
                gamificationScoresPerUser[user] = []
            }

            gamificationScoresPerUser[user].push(gamificationScore)
        })

        return gamificationScoresPerUser
    }

    async loadGamificationBadgesPerUser() {
        // Get gamification badges
        const gamificationBadges = await GamificationBadgeAPI.getAll({
            'user.company': this.$currentCompany.id,
        })

        // Get gamification badges indexed per user
        const gamificationBadgesPerUser = {}

        gamificationBadges.forEach((gamificationBadge: GamificationBadge) => {
            const user = gamificationBadge.user instanceof User ? gamificationBadge.user.id : gamificationBadge.user as string
            if (!(user in gamificationBadgesPerUser)) {
                gamificationBadgesPerUser[user] = []
            }

            gamificationBadgesPerUser[user].push(gamificationBadge)
        })

        return gamificationBadgesPerUser
    }

    async reloadCompanySettings(): Promise<void> {
        this.$currentCompany.gamificationSettings = await GamificationSettingsAPI.get(
            this.$currentCompany.id,
        )
    }

    badgesRankFilter(badges: GamificationBadge[], rank: number) {
        return badges.filter(badge => badge.rank === rank)
    }

    colorForBadgeType(badgeType: BadgeType): BadgeColor {
        switch (badgeType) {
            case BadgeType.FASTEST_CONVERSION:
            case BadgeType.FASTEST_LEAD_PICKUP:
                return BadgeColor.GREEN
            case BadgeType.HIGHEST_LEAD_QUALITY:
            case BadgeType.HIGHEST_REVENUE:
                return BadgeColor.PINK
            case BadgeType.MOST_APPOINTMENT:
            case BadgeType.MOST_CONVERSION:
            case BadgeType.MOST_CREATED_LEADS:
            case BadgeType.MOST_PROPOSALS:
                return BadgeColor.BLUE
            default:
                return BadgeColor.GREEN
        }
    }

    rankColorForBadge(rank: number) {
        switch (rank) {
            case 1:
                return BadgeColor.GOLD
            case 2:
                return BadgeColor.SILVER
            case 3:
                return BadgeColor.BRONZE
            default:
                return BadgeColor.GOLD
        }
    }

    textStyleForBadge(badge: GamificationBadge): object {
        return {
            color: BadgeColorCodes[this.rankColorForBadge(badge.rank)],
        }
    }

    isEnabled(badgeType: BadgeType): boolean {
        if (!this.$currentCompany.gamificationSettings) {
            return false
        }

        return this.$currentCompany.gamificationSettings.activatedBadges.includes(badgeType)
    }

    backgroundStyleForRank(rank: number): object {
        return {
            'background-color': BadgeColorCodes[this.rankColorForBadge(rank)],
        }
    }

    get highestScore(): GamificationScore {
        return this.getUserScores(this.$currentUser).filter(score => {
            return score.year === this.currentYear && score.quarter === this.currentQuarter && score.relativeScore !== 100
        }).sort((a, b) => {
            return a.relativeScore > b.relativeScore ? 1 : -1
        }).pop()
    }

    getUserScores(user: User): GamificationScore[] {
        if (!user.gamificationScores) {
            return []
        }

        return user.gamificationScores.filter(score => {
            return this.isEnabled(score.type)
        })
    }

    getUserBadges(user: User): GamificationBadge[] {
        if (!user.gamificationBadges) {
            return []
        }

        return user.gamificationBadges.filter(badge => {
            return this.isEnabled(badge.type)
        })
    }

    getQuarterStartDate(badge: GamificationBadge): Date {
        const month = 1 + (3 * (badge.quarter - 1))

        const quarterStartDate = new Date()
        quarterStartDate.setFullYear(badge.year)
        quarterStartDate.setHours(0, 0, 0, 0)
        quarterStartDate.setMonth(month, 14)

        return quarterStartDate
    }

    getHideBadgeDate(badge: GamificationBadge): Date {
        const hideBadgeDate = new Date(this.getQuarterStartDate(badge))
        hideBadgeDate.setDate(14)

        return hideBadgeDate
    }

    get gamificationEnabled(): boolean {
        if (!this.$currentCompany.gamificationSettings) {
            return false
        }

        return this.$currentCompany.gamificationSettings.enabled
    }

    get badgeTypes(): BadgeType[] {
        return BadgeTypes.filter(badgeType => {
            return this.isEnabled(badgeType)
        })
    }

    get badges(): GamificationBadge[] {
        return this.getUserBadges(this.$currentUser)
    }

    get rank(): number {
        return this.users.findIndex(user => {
            return this.$currentUser.id === user.id
        }) + 1
    }

    get lastReceivedBadge(): null|GamificationBadge {
        const badges = this.badges.sort((a: GamificationBadge, b: GamificationBadge) => {
            return a.year > b.year || (a.year === b.year && a.quarter > b.quarter) ? 1 : -1
        })

        return badges.length > 0  ? badges[badges.length - 1] : null
    }

    get percentageOfReceivedBadges(): number {
        const list = []

        this.badges.forEach(badge => {
            if (!list.includes(badge.type) && this.isEnabled(badge.type)) {
                list.push(badge.type)
            }
        })

        return 100 / this.badgeTypes.length * list.length
    }

    get users(): User[] {
        let users = this.$currentCompany.users.filter(user => this.getUserBadges(user).length > 0)
        if (users.length === 0) {
            users = this.$currentCompany.getAvailableUsers()
        }

        return users.sort((a, b) => {
            const totals = { points: 0, rank1: 0, rank2: 0, rank3: 0 }
            const totalsA = { ...totals }
            const totalsB = { ...totals }

            this.getUserBadges(a).forEach(badge => {
                totalsA.points += badge.points
                totalsA[`rank${badge.rank}`]++
            })

            this.getUserBadges(b).forEach(badge => {
                totalsB.points += badge.points
                totalsB[`rank${badge.rank}`]++
            })

            for (const key of Object.keys(totals)) {
                if (totalsB[key] !== totalsA[key]) {
                    return totalsB[key] - totalsA[key]
                }
            }

            return a.name.localeCompare(b.name)
        })
    }

    get receivedBadgeLast14Days(): boolean {
        if (!this.lastReceivedBadge) {
            return false
        }

        const currentDate = new Date()
        const quarterStartDate = this.getQuarterStartDate(this.lastReceivedBadge)
        const hideBadgeDate = this.getHideBadgeDate(this.lastReceivedBadge)

        return currentDate > quarterStartDate && currentDate < hideBadgeDate
    }

    get currentQuarter(): number {
        const date = new Date()
        const quarter = Math.floor((date.getMonth() + 3) / 3)

        return quarter
    }

    get currentYear(): number {
        const date = new Date()

        return date.getFullYear()
    }
}

